/**
 * calculationService.js
 * PSP/CRE Tool — Calculation Service Layer
 *
 * VERSION 1.1.0 (March 18, 2026) — BUG-161: localStorage quota fix.
 *   Root cause: storeBaseline(scenario, results) deep-copies BOTH the full scenario
 *   (which already contains casualty_projections from the previous run) AND the new
 *   results. Combined size routinely exceeded the ~5 MB localStorage quota, causing
 *   lsSet() to catch QuotaExceededError silently and return false. getBaseline()
 *   then returned null, blocking Tab 6 with "No Baseline Found".
 *   Fix: strip scenario.casualty_projections and scenario.calculation_results from
 *   the scenario copy before storing — they are redundant (results stored separately).
 *   This alone typically halves the stored payload.
 *   Tab5 now checks the storeBaseline() return value and surfaces a warning snackbar
 *   if storage still fails after pruning (v4.20.0).
 * VERSION 1.0.0 (March 8, 2026)
 * Author: Dr. Tatar Radu MD
 *
 * PURPOSE:
 *   Bridge between Tabs 1–5 (configuration + calculation) and Tab 6 (COA planning).
 *   Preserves the baseline scenario + results so Tab 6 can create variants without
 *   overwriting the original calculation. Also provides a dirty flag so Tab 5 can
 *   warn the user when Tab 1–4 configuration has changed since the last calculation.
 *
 * DOCTRINAL FRAMING:
 *   Tab 5 = mission analysis ("what I have"). Runs the calculation, stores baseline.
 *   Tab 6 = COA development ("what I intend to do"). Reads baseline, creates variants.
 *   Calculation outputs appear in both — serving different purposes.
 *
 * ARCHITECTURE:
 *   - Pure JS module — no React, no UI, no side effects beyond localStorage.
 *   - Does NOT modify or replace casualtyCalculator.js. Calculator unchanged.
 *   - Tab 5 remains the owner of the "Calculate" action.
 *   - Tab 4 Save & Continue calls markDirty() only.
 *   - Tab 6 calls calculateVariant() or storeVariant() to build COA comparisons.
 *
 * LOCALSTORAGE KEYS:
 *   psp_calculation_baseline        — { scenario, results, timestamp, version }
 *   psp_calculation_dirty           — 'true' | 'false'
 *   psp_calculation_variant_{id}    — { id, name, modifications, scenario, results, timestamp }
 *   psp_calculation_variant_index   — JSON array of variant IDs
 *
 * API SURFACE:
 *   Baseline:
 *     storeBaseline(scenario, results)  — Tab 5 calls after successful calculation
 *     getBaseline()                     — Tab 6 reads; returns null if none
 *     hasBaseline()                     — boolean check
 *     clearBaseline()                   — reset (use with caution)
 *
 *   Dirty flag:
 *     markDirty()                       — Tab 4 Save & Continue calls
 *     clearDirty()                      — Tab 5 calls after successful calculation
 *     isDirty()                         — Tab 5 reads to show warning
 *
 *   Variants (Tab 6):
 *     storeVariant(id, name, modifications, scenario, results)
 *     getVariant(id)
 *     listVariants()                    — returns array of { id, name, timestamp }
 *     deleteVariant(id)
 *     clearVariants()
 *
 *   Utilities:
 *     clearAll()                        — full reset (new scenario)
 *     getStatus()                       — diagnostic summary object
 *
 * CONSTRAINTS:
 *   - Max 5 variants stored simultaneously (Tab 6 UI constraint).
 *   - Baseline scenario is a deep copy — modifying Tab 1–4 after storeBaseline()
 *     does NOT retroactively change the stored baseline.
 *   - Variants are stored independently — deleting a variant never affects baseline
 *     or other variants.
 *   - All localStorage writes are guarded; failures are logged but never throw.
 *
 * ZERO ESINT VIOLATIONS: No unused vars, no undef, no console.error in production paths.
 */

// BUG-FF-012: LZ-string compression for localStorage quota fix
import { lsWrite, lsRead, lsRemove } from '../utils/lsStore.js';

// ============================================================================
// CONSTANTS
// ============================================================================

const STORAGE_KEYS = {
  BASELINE:       'psp_calculation_baseline',
  DIRTY:          'psp_calculation_dirty',
  VARIANT_PREFIX: 'psp_calculation_variant_',
  VARIANT_INDEX:  'psp_calculation_variant_index',
};

// STORAGE-SPLIT: Calculation results (~2.5 MB) are stored in sessionStorage, NOT localStorage.
//
// WHY: localStorage quota on Safari/localhost is ~5 MB. Combined scenario data already uses
// ~1.5 MB (psp_cre_current_scenario_v2 ~700 KB + psp_logistics_projection ~818 KB).
// A baseline of 3.3 MB leaves only ~189 KB headroom — not enough given Safari's internal
// accounting overhead. Splitting results into sessionStorage (separate, larger quota) keeps
// localStorage free for scenario data and metadata.
//
// sessionStorage survives page REFRESH but is cleared on tab CLOSE. On a new browser
// session the user simply hits RECALCULATE in Tab 5 to restore results in Tab 6.
// All scenario/config data persists in localStorage as before.
const SESSION_KEYS = {
  RESULTS: 'psp_calculation_results',
};

// Tab 6 exercise cache key — must stay in sync with EXERCISE_STORAGE_KEYS.BASELINE in Tab6_COAAnalysis.jsx.
const EXERCISE_BASELINE_KEY = 'psp_exercise_baseline';

const MAX_VARIANTS = 8;

const SERVICE_VERSION = '1.1.1';

// ============================================================================
// INTERNAL HELPERS
// ============================================================================

/**
 * Deep copy a value using JSON round-trip.
 * Returns null on failure (e.g. circular references, non-serializable values).
 */
function deepCopy(value) {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return null;
  }
}

/**
 * Safe localStorage write with LZ-string compression (BUG-FF-012).
 * Returns true on success.
 */
function lsSet(key, value) {
  return lsWrite(key, value);
}

/**
 * Safe localStorage read with LZ-string decompression + legacy fallback (BUG-FF-012).
 * Returns null on failure or missing key.
 */
function lsGet(key) {
  return lsRead(key);
}

/**
 * Safe localStorage delete.
 */
function lsDelete(key) {
  lsRemove(key);
}

// ============================================================================
// SESSION STORAGE HELPERS (for large results)
// ============================================================================

/** Safe sessionStorage write. Returns true on success. */
function ssSet(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

/** Safe sessionStorage read. Returns null on failure or missing key. */
function ssGet(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw === null) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Safe sessionStorage delete. */
function ssDelete(key) {
  try {
    sessionStorage.removeItem(key);
  } catch { /* ignore */ }
}

// ============================================================================
// BASELINE
// ============================================================================

/**
 * Store a completed calculation as the baseline.
 * Called by Tab 5 after every successful calculateCasualties() run.
 *
 * @param {Object} scenario  — full scenario object at time of calculation
 * @param {Object} results   — return value of calculateCasualties(scenario, { final: true })
 * @returns {boolean}        — true if stored successfully
 */
export function storeBaseline(scenario, results) {
  // Strip calculation outputs — never stored in the baseline snapshot.
  const scenarioForStorage = { ...scenario };
  delete scenarioForStorage.casualty_projections;
  delete scenarioForStorage.calculation_results;

  // STORAGE-SPLIT — two-tier design to stay well within Safari/localhost localStorage quota:
  //
  //  sessionStorage  psp_calculation_results  (~2.5–4 MB)
  //    results          — full calculateCasualties() output (Monte Carlo, supply chain, etc.)
  //    forceBuilderResult — full corps unit hierarchy tree (~500–1500 KB)
  //    Cleared on browser-tab close; regenerated by RECALCULATE in Tab 5.
  //
  //  localStorage    psp_calculation_baseline  (~100–300 KB)
  //    scenario WITHOUT forceBuilderResult — config params, medical plan, logistics
  //    has_results: true — tells getBaseline() to look in sessionStorage
  //    Persists across browser close/reopen so Tab 6 knows a baseline exists.
  //
  // Why strip forceBuilderResult from localStorage?
  //   The corps unit hierarchy for a multinational corps can be 500–1500 KB on its own.
  //   psp_cre_current_scenario_v2 (~700 KB) + psp_logistics_projection (~818 KB) already
  //   consume ~1.5 MB. Adding even a scenario-only baseline with forceBuilderResult
  //   pushes the total past Safari/localhost's effective quota (~2–3 MB observed limit).
  //   Moving it to sessionStorage reduces the localStorage baseline to ~100–300 KB,
  //   giving ample headroom. Tab 6 re-hydrates it from sessionStorage on every call to
  //   getBaseline() during the same browser session.

  // Extract forceBuilderResult before deep-copying scenario for localStorage.
  const forceBuilderResult = scenarioForStorage.force?.forceBuilderResult ?? null;
  const slimForce = scenarioForStorage.force
    ? { ...scenarioForStorage.force, forceBuilderResult: undefined }
    : scenarioForStorage.force;
  const slimScenario = { ...scenarioForStorage, force: slimForce };

  const scenarioCopy = deepCopy(slimScenario);
  if (!scenarioCopy) return false;

  // Write large data to sessionStorage (separate quota — not counted against localStorage).
  ssDelete(SESSION_KEYS.RESULTS);
  ssSet(SESSION_KEYS.RESULTS, { results, forceBuilderResult });

  const baseline = {
    scenario:    scenarioCopy,   // slim — no forceBuilderResult, no projections
    has_results: true,           // getBaseline() will merge from sessionStorage
    timestamp:   new Date().toISOString(),
    version:     SERVICE_VERSION,
  };

  // Purge stale large keys before writing (Safari quota accounting bug fix).
  lsDelete(STORAGE_KEYS.BASELINE);
  lsDelete(EXERCISE_BASELINE_KEY);

  const stored = lsSet(STORAGE_KEYS.BASELINE, baseline);
  if (stored) {
    lsSet(STORAGE_KEYS.DIRTY, false);
  }
  return stored;
}

/**
 * Retrieve the stored baseline.
 * Merges the slim localStorage snapshot with sessionStorage results + forceBuilderResult.
 * Returns null if no baseline has been stored yet.
 *
 * @returns {{ scenario, results, timestamp, version } | null}
 */
export function getBaseline() {
  const baseline = lsGet(STORAGE_KEYS.BASELINE);
  if (!baseline) return null;

  if (baseline.has_results) {
    // Re-hydrate results and forceBuilderResult from sessionStorage.
    const session = ssGet(SESSION_KEYS.RESULTS);
    const rehydratedScenario = (baseline.scenario && session?.forceBuilderResult)
      ? {
          ...baseline.scenario,
          force: baseline.scenario.force
            ? { ...baseline.scenario.force, forceBuilderResult: session.forceBuilderResult }
            : baseline.scenario.force
        }
      : baseline.scenario;
    return {
      ...baseline,
      scenario: rehydratedScenario,
      results:  session?.results ?? null,
    };
  }

  // Legacy path: old baselines stored results directly in localStorage.
  return baseline;
}

/**
 * Returns true if a baseline has been stored.
 */
export function hasBaseline() {
  return lsGet(STORAGE_KEYS.BASELINE) !== null;
}

/**
 * Delete the stored baseline.
 * Also clears the dirty flag and all variants (baseline is the root).
 */
export function clearBaseline() {
  lsDelete(STORAGE_KEYS.BASELINE);
  lsDelete(STORAGE_KEYS.DIRTY);
  clearVariants();
}

// ============================================================================
// DIRTY FLAG
// ============================================================================

/**
 * Mark the baseline as stale.
 * Called by Tab 4 Save & Continue whenever configuration changes after a
 * baseline has been stored. Tab 5 reads this flag to show a warning.
 *
 * Only marks dirty if a baseline already exists — no baseline means no warning needed.
 */
export function markDirty() {
  if (!hasBaseline()) return;
  lsSet(STORAGE_KEYS.DIRTY, true);
}

/**
 * Clear the dirty flag.
 * Called by Tab 5 after a successful calculation (storeBaseline() does this too,
 * but clearDirty() is available for edge cases).
 */
export function clearDirty() {
  lsSet(STORAGE_KEYS.DIRTY, false);
}

/**
 * Returns true if the stored baseline is stale (Tab 1–4 changed since last calculation).
 * Tab 5 uses this to render a warning Alert.
 */
export function isDirty() {
  return lsGet(STORAGE_KEYS.DIRTY) === true;
}

// ============================================================================
// VARIANTS (Tab 6)
// ============================================================================

/**
 * Store a COA variant.
 * Tab 6 calls this after recalculating a modified scenario snapshot.
 *
 * @param {string} id            — unique variant ID (e.g. 'variant_001')
 * @param {string} name          — human label (e.g. 'More Blood +50%')
 * @param {Array}  modifications — array of { type, field, change, value } — QM-001..QM-010
 * @param {Object} scenario      — modified scenario snapshot used for recalculation
 * @param {Object} results       — output of calculateCasualties(modifiedScenario)
 * @returns {boolean}            — true if stored; false if at max variants or copy failed
 */
export function storeVariant(id, name, modifications, scenario, results) {
  const index = listVariants();

  // Enforce max variants limit
  if (!index.find(v => v.id === id) && index.length >= MAX_VARIANTS) {
    return false;
  }

  const copy = deepCopy({ scenario, results });
  if (!copy) return false;

  const variant = {
    id,
    name:          name || id,
    modifications: deepCopy(modifications) || [],
    scenario:      copy.scenario,
    results:       copy.results,
    timestamp:     new Date().toISOString(),
    version:       SERVICE_VERSION,
  };

  const stored = lsSet(`${STORAGE_KEYS.VARIANT_PREFIX}${id}`, variant);
  if (!stored) return false;

  // Update index
  const newIndex = index.filter(v => v.id !== id);
  newIndex.push({ id, name: variant.name, timestamp: variant.timestamp });
  lsSet(STORAGE_KEYS.VARIANT_INDEX, newIndex);

  return true;
}

/**
 * Retrieve a stored variant by ID.
 * Returns null if not found.
 *
 * @param {string} id
 * @returns {{ id, name, modifications, scenario, results, timestamp } | null}
 */
export function getVariant(id) {
  return lsGet(`${STORAGE_KEYS.VARIANT_PREFIX}${id}`);
}

/**
 * List all stored variants (metadata only — no scenario/results payload).
 * Returns array of { id, name, timestamp }, sorted oldest first.
 *
 * @returns {Array<{ id: string, name: string, timestamp: string }>}
 */
export function listVariants() {
  return lsGet(STORAGE_KEYS.VARIANT_INDEX) || [];
}

/**
 * Delete a single variant by ID.
 */
export function deleteVariant(id) {
  lsDelete(`${STORAGE_KEYS.VARIANT_PREFIX}${id}`);
  const index = listVariants().filter(v => v.id !== id);
  lsSet(STORAGE_KEYS.VARIANT_INDEX, index);
}

/**
 * Delete all stored variants.
 * Does NOT affect the baseline.
 */
export function clearVariants() {
  const index = listVariants();
  index.forEach(v => lsDelete(`${STORAGE_KEYS.VARIANT_PREFIX}${v.id}`));
  lsDelete(STORAGE_KEYS.VARIANT_INDEX);
}

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Full reset — clears baseline, dirty flag, and all variants.
 * Call when the user starts a completely new scenario.
 */
export function clearAll() {
  clearBaseline(); // also calls clearVariants() and clears dirty
}

/**
 * Diagnostic summary — for Tab 6 status display and debugging.
 *
 * @returns {{
 *   hasBaseline: boolean,
 *   baselineTimestamp: string | null,
 *   isDirty: boolean,
 *   variantCount: number,
 *   maxVariants: number,
 *   variants: Array<{ id, name, timestamp }>,
 *   version: string
 * }}
 */
export function getStatus() {
  const baseline = getBaseline();
  const variants = listVariants();

  return {
    hasBaseline:       baseline !== null,
    baselineTimestamp: baseline?.timestamp || null,
    isDirty:           isDirty(),
    variantCount:      variants.length,
    maxVariants:       MAX_VARIANTS,
    variants,
    version:           SERVICE_VERSION,
  };
}

/**
 * Maximum number of variants allowed simultaneously.
 * Exported so Tab 6 UI can enforce the limit before calling storeVariant().
 */
export { MAX_VARIANTS };
